// Integrytas Group - Common Security Module (CSM)
// PersonalizationProvider — supplies per-page personalization, backed by the
// unified tiered ULD store. One personalization mode edits a chosen tier
// (System / Role / User): reads layer the tiers BELOW the edit tier under the
// caller's own draft for that tier, and saves the draft to the edit tier. The
// public context API is unchanged for PersonalizedField / PersonalizedGrid; the
// tier fields are additive (surfaced by the toggle's tier selector).

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { asfWorkspaceCode } from '../../lib/asf';
import { csmLngTag, DEFAULT_CSM_LOCALE } from '../../i18n';
import { saveUldDesign, useDesignTier, tierErrorMessage, emitUldChanged, type UldTier } from '../../hooks/use_uld_layout';
import { applyLayoutDelta } from '../../lib/uld_merge';
import type { UldDeltaAction, UldActionType } from '../../lib/uld_merge';
import { PersonalizationContext, type PersonalizationCtx } from '../../lib/use_personalization';
import { useShellChrome } from '../../lib/shell_chrome';
import type { AuthSession } from '../../types/auth';
import type { FieldPreference, ColumnPreference } from '../../types/personalization';
import type { FieldLineage, InspectSelection } from '../../types/fln';
import { DEFAULT_FIELD_PREF } from '../../types/personalization';
import { FieldLineagePanel } from './FieldLineagePanel';

interface Props {
  pageCode: string;
  session:  AuthSession;
  children: ReactNode;
}

type DeltaMap = Record<string, UldDeltaAction[]>;

// Replace any existing (action, component_key) entry, then append the new one.
function upsertAction(delta: UldDeltaAction[], action: UldActionType, componentKey: string, patch: Partial<UldDeltaAction>): UldDeltaAction[] {
  const next = delta.filter(a => !(a.action === action && a.component_key === componentKey));
  next.push({ action, component_key: componentKey, ...patch });
  return next;
}

function deriveFieldPref(actions: UldDeltaAction[], fieldKey: string): FieldPreference {
  const p: FieldPreference = { ...DEFAULT_FIELD_PREF };
  for (const a of actions) {
    if (a.component_key !== fieldKey) continue;
    if (a.action === 'hide_element') p.visible = false;
    else if (a.action === 'set_visible'  && typeof a.visible  === 'boolean') p.visible  = a.visible;
    else if (a.action === 'set_readonly' && typeof a.readonly === 'boolean') p.readonly = a.readonly;
    else if (a.action === 'set_required' && typeof a.required === 'boolean') p.required = a.required;
  }
  return p;
}

function deriveColPrefs(actions: UldDeltaAction[], defaultKeys: string[]): Record<string, ColumnPreference> {
  const core = defaultKeys.map((k, i) => ({ key: k, order: i, visible: true }));
  const { layout } = applyLayoutDelta(core, actions);
  const out: Record<string, ColumnPreference> = {};
  layout.forEach((it, idx) => {
    out[it.key] = {
      visible: it.visible !== false,
      width: typeof it.width === 'number' ? it.width : undefined,
      order: typeof it.order === 'number' ? it.order : idx,
    };
  });
  return out;
}

// Default editing target: a super-user (System available ⇒ admin) edits System;
// everyone else defaults to their own (most personal) tier.
function preferredTier(available: UldTier[]): UldTier {
  if (available.includes('SYSTEM')) return 'SYSTEM';
  if (available.includes('USER')) return 'USER';
  if (available.includes('ROLE')) return 'ROLE';
  return 'SYSTEM';
}

export function PersonalizationProvider({ pageCode, session, children }: Props) {
  const ws = asfWorkspaceCode() || '';
  const { i18n } = useTranslation();
  const activeLocale = csmLngTag(i18n.language || DEFAULT_CSM_LOCALE);
  const { available, roleOwner, loading: tiersLoading } = useDesignTier(pageCode, ws);

  const [belowMap, setBelowMap] = useState<DeltaMap>({});   // tiers below editTier
  const [ownDraft, setOwnDraft] = useState<DeltaMap>({});   // editable delta at editTier
  const dirty = useRef<Set<string>>(new Set());
  // Shell chrome bridge: global inspect + which tab is active. Absent when the
  // page renders standalone (tests) — then inspect falls back to local state.
  const chrome = useShellChrome();
  const isActive = chrome ? chrome.activeCode === pageCode : true;

  const [localInspecting, setLocalInspecting] = useState(false);
  const isInspecting = chrome ? chrome.isInspecting : localInspecting;
  const setInspecting = chrome ? chrome.setInspecting : setLocalInspecting;

  const [isPersonalizing, setPersonalizing] = useState(false);
  // Personalize and Inspect are mutually exclusive modes — entering one exits the other.
  const togglePersonalizing = useCallback(() => { setInspecting(false); setPersonalizing(p => !p); }, [setInspecting]);
  const stopPersonalizing = useCallback(() => setPersonalizing(false), []);

  // ---- Field-lineage "Inspect" mode (FLN) ---------------------------------
  const [inspectSelection, setInspectSelection] = useState<InspectSelection | null>(null);
  const [lineageMap, setLineageMap] = useState<Map<string, FieldLineage> | null>(null);
  const [inspectLoading, setInspectLoading] = useState(false);
  const toggleInspecting = useCallback(() => {
    setPersonalizing(false);
    setInspecting(!isInspecting);
  }, [isInspecting, setInspecting]);
  const inspectField = useCallback((componentKey: string, fieldKey: string, label: string) =>
    setInspectSelection({ component_key: componentKey, field_key: fieldKey, label }), []);
  const clearInspectSelection = useCallback(() => setInspectSelection(null), []);
  const lineageFor = useCallback((componentKey: string, fieldKey: string): FieldLineage | undefined =>
    lineageMap?.get(`${componentKey}|${fieldKey}`), [lineageMap]);

  // Leaving Inspect (globally) drops this page's selection so the panel closes.
  useEffect(() => { if (!isInspecting) setInspectSelection(null); }, [isInspecting]);

  // Lazy-load the page's lineage while inspecting — but only for the ACTIVE tab,
  // so background tabs don't each fetch when the global switch turns on.
  useEffect(() => {
    if (!isInspecting || !isActive) return;
    let cancelled = false;
    setInspectLoading(true);
    void (async () => {
      const { data } = await apiDb.rpc<FieldLineage[]>('fln_field_lineage', { p_page_code: pageCode, p_locale: activeLocale });
      if (cancelled) return;
      const m = new Map<string, FieldLineage>();
      for (const row of (Array.isArray(data) ? data : [])) m.set(`${row.component_key}|${row.field_key}`, row);
      setLineageMap(m);
      setInspectLoading(false);
    })();
    return () => { cancelled = true; };
  }, [isInspecting, isActive, pageCode, activeLocale]);
  // "Show original" baseline preview. Kept SEPARATE from isPersonalizing so it
  // never triggers the exit-flush below; consumers see a derived "not editing"
  // state while the real session (and the user's draft) stays intact.
  const [previewBaseline, setPreviewBaseline] = useState(false);
  const startPreview = useCallback(() => setPreviewBaseline(true), []);
  const exitPreview  = useCallback(() => setPreviewBaseline(false), []);
  const [designPanelKey, setDesignPanelKey] = useState<string | null>(null);
  const registerDesignPanel = useCallback((key: string | null) => setDesignPanelKey(key), []);
  // Bumps whenever the edit baseline changes (tier load / reset) so design panels
  // and column managers re-seed their local editing state.
  const [resetTick, setResetTick] = useState(0);

  const [editTier, setEditTierState] = useState<UldTier>('USER');
  const [tierTouched, setTierTouched] = useState(false);
  // A manual tier pick sticks; until then we follow the preferred default.
  const setEditTier = useCallback((t: UldTier) => { setTierTouched(true); setEditTierState(t); }, []);
  useEffect(() => {
    if (available.length === 0) return;
    setEditTierState(prev =>
      tierTouched ? (available.includes(prev) ? prev : preferredTier(available)) : preferredTier(available));
  }, [available, tierTouched]);

  // Load the below-tier baseline + the caller's own delta for the edit tier.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const below = editTier === 'SYSTEM'
        ? { data: {} as DeltaMap }
        : await apiDb.rpc<DeltaMap>('uld_resolve_layout', {
            p_page_code: pageCode, p_workspace_code: ws,
            p_include_user: false, p_include_role: editTier === 'USER',
          });
      const own = await apiDb.rpc<DeltaMap>('uld_my_delta', {
        p_page_code: pageCode, p_workspace_code: ws, p_scope_type: editTier,
      });
      if (cancelled) return;
      setBelowMap(below.data && typeof below.data === 'object' ? below.data : {});
      setOwnDraft(own.data && typeof own.data === 'object' ? own.data : {});
      dirty.current = new Set();
      setResetTick(t => t + 1);   // re-seed panels/grids for the freshly-loaded tier
    })();
    return () => { cancelled = true; };
  }, [pageCode, ws, editTier]);

  // While previewing the original, drop the edit tier's own draft so only the
  // tiers immediately above (below in precedence) apply — the page as it would
  // look without any personalization at the level being changed.
  const componentDelta = useCallback(
    (componentKey: string): UldDeltaAction[] => previewBaseline
      ? [...(belowMap[componentKey] ?? [])]
      : [...(belowMap[componentKey] ?? []), ...(ownDraft[componentKey] ?? [])],
    [belowMap, ownDraft, previewBaseline],
  );

  const fieldPref = useCallback((componentKey: string, fieldKey: string) => deriveFieldPref(componentDelta(componentKey), fieldKey), [componentDelta]);
  const colPrefs  = useCallback((componentKey: string, defaultKeys: string[]) => deriveColPrefs(componentDelta(componentKey), defaultKeys), [componentDelta]);

  const setFieldPref = useCallback((componentKey: string, fieldKey: string, patch: Partial<FieldPreference>) => {
    if (previewBaseline) return;   // read-only while previewing the original
    setOwnDraft(prev => {
      let d = prev[componentKey] ?? [];
      if (patch.visible  !== undefined) d = upsertAction(d, 'set_visible',  fieldKey, { visible:  patch.visible });
      if (patch.readonly !== undefined) d = upsertAction(d, 'set_readonly', fieldKey, { readonly: patch.readonly });
      if (patch.required !== undefined) d = upsertAction(d, 'set_required', fieldKey, { required: patch.required });
      return { ...prev, [componentKey]: d };
    });
    dirty.current.add(componentKey);
  }, [previewBaseline]);

  const setColPrefs = useCallback((componentKey: string, cols: Record<string, Partial<ColumnPreference>>) => {
    if (previewBaseline) return;   // read-only while previewing the original
    setOwnDraft(prev => {
      let d = prev[componentKey] ?? [];
      for (const [colKey, patch] of Object.entries(cols)) {
        if (patch.order   !== undefined) d = upsertAction(d, 'reorder_index', colKey, { index: patch.order });
        if (patch.visible !== undefined) d = upsertAction(d, 'set_visible',   colKey, { visible: patch.visible });
        if (patch.width   !== undefined) d = upsertAction(d, 'set_col_width', colKey, { width: patch.width });
      }
      return { ...prev, [componentKey]: d };
    });
    dirty.current.add(componentKey);
  }, [previewBaseline]);

  const [lastError, setLastError] = useState<string | null>(null);
  const clearError = useCallback(() => setLastError(null), []);

  // "Reset" deletes a component's delta at the edit tier (so it falls back to the
  // tiers below / code defaults) rather than writing a default-valued row.
  const resetComponent = useCallback(async (componentKey: string) => {
    dirty.current.delete(componentKey);
    setOwnDraft(prev => { const { [componentKey]: _removed, ...next } = prev; return next; });
    setResetTick(t => t + 1);
    const { error } = await apiDb.rpc('uld_clear_design', {
      p_page_code: pageCode, p_component_key: componentKey, p_scope_type: editTier, p_workspace_code: ws,
    });
    if (error) setLastError(tierErrorMessage(error.message, roleOwner));
    else emitUldChanged(pageCode, ws);
  }, [pageCode, ws, editTier, roleOwner]);

  const flush = useCallback(async () => {
    const cks = [...dirty.current];
    if (cks.length === 0) return;
    dirty.current = new Set();
    const results = await Promise.all(cks.map(ck => saveUldDesign(pageCode, ck, editTier, ws, ownDraft[ck] ?? [])));
    const firstErr = results.find(r => r.error)?.error;
    // Surface a blocked save (e.g. a Role design owned by another role) instead
    // of failing silently — the user otherwise thinks the change persisted.
    if (firstErr) setLastError(tierErrorMessage(firstErr, roleOwner));
  }, [pageCode, ws, editTier, ownDraft, roleOwner]);

  // Persist the personalization when the user exits personalize mode.
  useEffect(() => { if (!isPersonalizing) void flush(); }, [isPersonalizing, flush]);
  // Leaving personalization (exit / tab switch) also drops any baseline preview.
  useEffect(() => { if (!isPersonalizing) setPreviewBaseline(false); }, [isPersonalizing]);

  // Consumers see "not editing" during a baseline preview, so all edit chrome
  // (field toggles, column manager, the toggle button) suppresses — without
  // touching the real isPersonalizing state that drives the exit-flush above.
  const exposedPersonalizing = isPersonalizing && !previewBaseline;

  const value = useMemo<PersonalizationCtx>(() => ({
    pageCode, session, isPersonalizing: exposedPersonalizing, togglePersonalizing, stopPersonalizing,
    isInspecting, toggleInspecting, inspectField, inspectSelection, clearInspectSelection, lineageFor, inspectLoading,
    fieldPref, setFieldPref, colPrefs, setColPrefs,
    designPanelKey, registerDesignPanel,
    availableTiers: available, tiersLoading, editTier, setEditTier, roleOwner, componentDelta,
    lastError, clearError, resetComponent, resetTick,
    previewBaseline, startPreview, exitPreview, flush,
   
  }), [pageCode, session, exposedPersonalizing, togglePersonalizing, stopPersonalizing,
       isInspecting, toggleInspecting, inspectField, inspectSelection, clearInspectSelection, lineageFor, inspectLoading,
       fieldPref, setFieldPref, colPrefs, setColPrefs, designPanelKey, registerDesignPanel,
       available, tiersLoading, editTier, setEditTier, roleOwner, componentDelta, lastError, clearError, resetComponent, resetTick,
       previewBaseline, startPreview, exitPreview, flush]);

  // Publish this page's personalization to the title bar while it is the active
  // tab, so the header renders a single control bound to the shown page. The ctx
  // `value` is a fresh object each render, so we publish the LATEST via a ref and
  // only re-run on the primitive state the header control actually reflects —
  // depending on `value` itself would loop (publish → shell setState → re-render).
  const publishPersonalization = chrome?.publishPersonalization;
  const valueRef = useRef(value);
  valueRef.current = value;
  const tiersKey = available.join(',');
  useEffect(() => {
    if (!publishPersonalization || !isActive) return;
    publishPersonalization(pageCode, valueRef.current);
    return () => publishPersonalization(pageCode, null);
  }, [publishPersonalization, isActive, pageCode,
      exposedPersonalizing, tiersKey, tiersLoading, editTier, roleOwner?.role_id, lastError, previewBaseline]);

  return (
    <PersonalizationContext.Provider value={value}>
      {previewBaseline && (
        <div className="fixed inset-x-0 top-0 z-[100] flex justify-center pointer-events-none">
          <div className="pointer-events-auto mt-2 flex items-center gap-3 rounded border border-amber-300 bg-amber-50 px-3 py-1.5 text-csm-sm text-amber-800 shadow-md">
            <span className="font-medium">⚑ Previewing original — your changes are hidden</span>
            <button
              type="button"
              onClick={exitPreview}
              className="rounded border border-amber-400 bg-white px-2 py-0.5 text-csm-xs font-medium text-amber-800 hover:bg-amber-100"
            >
              Exit preview
            </button>
          </div>
        </div>
      )}
      {children}
      {isInspecting && inspectSelection && (
        <FieldLineagePanel
          selection={inspectSelection}
          lineage={lineageFor(inspectSelection.component_key, inspectSelection.field_key)}
          loading={inspectLoading}
          onClose={clearInspectSelection}
        />
      )}
    </PersonalizationContext.Provider>
  );
}
